// views/Dashboard/PainelExecutivo/sections/ceo/GeracaoDoMes.tsx
//
// "Usinas com meta de geração e percentual atingido da meta" — em duas formas:
//
//   ESTE MÊS   cada usina contra a meta até hoje, agrupada pelo conjunto
//              (Vera Cruz, Morro Agudo, Centro-Sul), que é como o Igor pensa;
//   MÊS A MÊS  usina × mês, com o % de cada mês pintado pela faixa — de relance
//              dá para ver quem está sempre abaixo e quem só teve um mês ruim.
//
// O título já diz a conclusão ("Geração em 90% da meta"), não o nome do bloco.
// As paradas foram para um bloco próprio, explicadas: a faixa de quadradinhos
// por dia que ficava aqui mostrava QUANDO parou, mas não POR QUÊ.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarRange, Sun, TrendingDown, TrendingUp } from 'lucide-react';
import {
  faixaDoAtingimento, geracaoPorUsina, matrizGeracao, produtividadeDoLaudo, projetarMes, totalGeracao, totalProjecao,
} from '../../../../../src/lib/dashboard/painelCeo';
import type { CelulaGeracao, DiaGeracao, EventoParada, LinhaVsMeta, ProjecaoUsina, UsinaCeo } from '../../../../../src/lib/dashboard/painelCeo';
import type { GeradoraSemReportar } from '../../../../../types';
import {
  Alternador, BarraMeta, Bloco, COR_FAIXA, Destaque, ErroBloco, FUNDO_FAIXA, LinhasCarregando, Surgir, horas, kwhCurto, pct,
} from './ui';

interface Props {
  usinas: UsinaCeo[];
  fator: number;
  /** 12 meses de `get_geracao_vs_meta`, o corrente incluído. */
  linhas: LinhaVsMeta[];
  /** Geração dia a dia dos últimos 7 dias completos: o ritmo da projeção. */
  diarios: DiaGeracao[];
  paradasAgora: GeradoraSemReportar[];
  eventos: EventoParada[];
  loading: boolean;
  erro: string | null;
  agora: Date;
}

type Visao = 'mes' | 'historico';

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const rotuloMes = (iso: string) => `${MES_CURTO[Number(iso.slice(5, 7)) - 1]}/${iso.slice(2, 4)}`;

interface Conjunto {
  nome: string;
  idx: number[];
  realKwh: number;
  metaKwh: number;
  atingimento: number | null;
  projecao: ReturnType<typeof totalProjecao>;
}

const GeracaoDoMes: React.FC<Props> = ({ usinas, fator, linhas, diarios, paradasAgora, eventos, loading, erro, agora }) => {
  const [visao, setVisao] = useState<Visao>('mes');
  const mesAtualIso = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-01`;
  const nomeMes = agora.toLocaleDateString('pt-BR', { month: 'long' });

  const doMes = useMemo(() => linhas.filter((l) => String(l.mes_ref ?? '').slice(0, 10) === mesAtualIso), [linhas, mesAtualIso]);
  const geracao = useMemo(() => geracaoPorUsina(usinas, doMes, fator), [usinas, doMes, fator]);
  const total = useMemo(() => totalGeracao(geracao), [geracao]);
  const produtividade = useMemo(() => produtividadeDoLaudo(usinas, doMes), [usinas, doMes]);
  const matriz = useMemo(() => matrizGeracao(usinas, linhas, fator), [usinas, linhas, fator]);
  const diasNoMes = new Date(agora.getFullYear(), agora.getMonth() + 1, 0).getDate();
  const projecao = useMemo(() => projetarMes(usinas, doMes, diarios, diasNoMes, fator), [usinas, doMes, diarios, diasNoMes, fator]);
  const projTotal = useMemo(() => totalProjecao(projecao), [projecao]);

  // Conjuntos na ordem em que aparecem, com o total de cada um no mês.
  const conjuntos: Conjunto[] = useMemo(() => {
    const nomes = [...new Set(usinas.map((u) => u.conjunto))];
    return nomes.map((nome) => {
      const idx = usinas.map((u, i) => (u.conjunto === nome ? i : -1)).filter((i) => i >= 0);
      return { nome, idx, ...totalGeracao(idx.map((i) => geracao[i])), projecao: totalProjecao(idx.map((i) => projecao[i])) };
    });
  }, [usinas, geracao, projecao]);

  const horasPorUsina = useMemo(() => {
    const m = new Map<string, number>();
    eventos.forEach((e) => {
      const porUsina = e.horasUsina / Math.max(1, e.usinaIds.length);
      e.usinaIds.forEach((id) => m.set(id, (m.get(id) ?? 0) + porUsina));
    });
    return m;
  }, [eventos]);

  // O subtítulo diz onde está o problema, numa frase.
  const abaixo = conjuntos.filter((c) => c.atingimento != null && c.atingimento < 0.95);
  const acima = conjuntos.filter((c) => c.atingimento != null && c.atingimento >= 1);
  const resumo = [
    abaixo.length ? `Abaixo da meta: ${abaixo.map((c) => `${c.nome} (${pct(c.atingimento)})`).join(', ')}` : null,
    acima.length ? `Acima: ${acima.map((c) => `${c.nome} (${pct(c.atingimento)})`).join(', ')}` : null,
  ].filter(Boolean).join(' · ');

  const nomePorSite = useMemo(() => new Map(usinas.map((u) => [u.siteId, u.nome])), [usinas]);
  const agoraTexto = paradasAgora.length === 0
    ? 'Agora: todas gerando'
    : `Agora: ${paradasAgora.map((p) => `${nomePorSite.get(p.site_id_api) ?? p.nome_usina} parada${p.horas_paradas ? ` há ${Math.round(p.horas_paradas)} h` : ''}`).join(' · ')}`;

  return (
    <Bloco
      titulo={loading || erro ? 'Geração das usinas' : `Geração em ${pct(total.atingimento)} da meta em ${nomeMes}`}
      subtitulo={loading ? 'Carregando a telemetria…' : resumo || 'Todas as usinas no ritmo da meta.'}
      acoes={
        <>
          <Alternador<Visao>
            rotulo="Período da geração"
            valor={visao}
            onChange={setVisao}
            opcoes={[
              { id: 'mes', rotulo: 'Este mês', Icone: Sun },
              { id: 'historico', rotulo: 'Mês a mês', Icone: CalendarRange },
            ]}
          />
          {!loading && (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className={`h-1.5 w-1.5 rounded-full ${paradasAgora.length === 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} aria-hidden />
              {agoraTexto}
            </span>
          )}
        </>
      }
      destaque={
        loading || erro ? null : (
          <Destaque
            valor={pct(total.atingimento)}
            tom={COR_FAIXA[faixaDoAtingimento(total.atingimento)].texto}
            legenda={total.atingimento == null ? 'até hoje'
              : total.atingimento < 1
                ? `até hoje: gerou ${pct(1 - total.atingimento)} menos que o previsto para estes ${agora.getDate()} dias`
                : `até hoje: gerou ${pct(total.atingimento - 1)} mais que o previsto para estes ${agora.getDate()} dias`}
          />
        )
      }
    >
      {erro ? (
        <ErroBloco mensagem={erro} />
      ) : loading ? (
        <LinhasCarregando linhas={6} rotulo="Somando a geração de cada usina…" />
      ) : (
        <Surgir key={visao}>
          {visao === 'mes' && <Veredito p={projTotal} nomeMes={nomeMes} />}
          {visao === 'mes' ? (
            <EsteMes usinas={usinas} conjuntos={conjuntos} geracao={geracao} projecao={projecao} horasPorUsina={horasPorUsina} />
          ) : (
            <MesAMes usinas={usinas} conjuntos={conjuntos.map((c) => c.nome)} matriz={matriz} mesAtualIso={mesAtualIso} />
          )}
          <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-gray-100 px-5 py-3 text-[11px] leading-relaxed text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <span className="max-w-3xl">
              * Só as usinas de Vera Cruz têm laudo de projeto (PVsyst). As outras usam a mesma produtividade
              {produtividade != null ? ` (${produtividade.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kWh por kWp esperados até hoje)` : ''} vezes a própria potência.
            </span>
            <Link to="/geradoras?aba=analise" className="inline-flex shrink-0 items-center gap-1 font-semibold transition-colors hover:text-primary dark:hover:text-primary-light">
              Detalhes em Geradoras <ArrowRight size={12} />
            </Link>
          </footer>
        </Surgir>
      )}
    </Bloco>
  );
};

// ─────────────── este mês ───────────────

const GRADE = 'md:grid md:grid-cols-[minmax(150px,1fr)_64px_minmax(180px,2fr)_150px_92px] md:items-center md:gap-5';

const EsteMes: React.FC<{
  usinas: UsinaCeo[];
  conjuntos: Conjunto[];
  geracao: ReturnType<typeof geracaoPorUsina>;
  projecao: ProjecaoUsina[];
  horasPorUsina: Map<string, number>;
}> = ({ usinas, conjuntos, geracao, projecao, horasPorUsina }) => {
  let ordem = 0;
  return (
    <div>
      <div className={`hidden border-y border-gray-100 bg-gray-50/70 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400 ${GRADE}`}>
        <span>Usina</span>
        <span className="text-right">Até hoje</span>
        <span>Gerado x meta até hoje</span>
        <span className="text-right" title="Onde o mês fecha se a usina continuar no ritmo dos últimos 7 dias">Fecha o mês em</span>
        <span className="text-right">Parou</span>
      </div>
      {conjuntos.map((c) => {
        const faixaC = faixaDoAtingimento(c.atingimento);
        return (
          <section key={c.nome} aria-label={c.nome}>
            {/* Linha do conjunto: o total do grupo, em destaque discreto. */}
            <div className={`flex items-baseline justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-2.5 dark:border-gray-700/70 dark:bg-gray-900/20 ${GRADE}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{c.nome}</p>
              <p className={`text-right text-sm font-bold tabular-nums ${COR_FAIXA[faixaC].texto}`}>{pct(c.atingimento)}</p>
              <p className="hidden text-xs tabular-nums text-gray-500 md:block dark:text-gray-400">{kwhCurto(c.realKwh)} de {kwhCurto(c.metaKwh)} previstos até hoje</p>
              <Fecha p={c.projecao.projecao} forte className="hidden md:flex" />
              <div className="hidden md:block" />
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
              {c.idx.map((i) => {
                const u = usinas[i];
                const g = geracao[i];
                const faixa = faixaDoAtingimento(g.atingimento);
                const h = horasPorUsina.get(u.id) ?? 0;
                const o = ordem++;
                return (
                  <li key={u.id} className={`px-5 py-3 transition-colors duration-150 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 ${GRADE}`}>
                    <div className="flex items-baseline justify-between gap-3 md:block">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{u.nome}</p>
                      <p className={`text-sm font-bold tabular-nums md:hidden ${COR_FAIXA[faixa].texto}`}>{pct(g.atingimento)}</p>
                    </div>
                    <p className={`hidden text-right text-sm font-bold tabular-nums md:block ${COR_FAIXA[faixa].texto}`}>{pct(g.atingimento)}</p>
                    <div className="mt-2 md:mt-0">
                      <BarraMeta fracao={g.atingimento} faixa={faixa} ordem={o} />
                      <p className="mt-1 text-[11px] tabular-nums text-gray-400">
                        {g.semTelemetria ? 'sem telemetria' : `${kwhCurto(g.realKwh)} de ${kwhCurto(g.metaKwh)}`}
                        {g.metaEstimada && <span title="Sem laudo próprio: meta pela produtividade de Vera Cruz"> *</span>}
                      </p>
                    </div>
                    <div className="mt-1.5 flex items-baseline justify-between gap-3 md:contents">
                      <Fecha p={projecao[i].projecao} detalhe={projecao[i]} />
                      <p className={`text-xs font-semibold tabular-nums md:text-right ${h > 0.05 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-300 dark:text-gray-600'}`}>
                        {h > 0.05 ? `${horas(h)} parada` : '—'}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

/** "Fecha o mês em 88%", com a seta para cima/baixo do 100%. */
const Fecha: React.FC<{ p: number | null; detalhe?: ProjecaoUsina; forte?: boolean; className?: string }> = ({ p, detalhe, forte, className = 'flex' }) => {
  if (p == null) return <span className={`${className} items-center justify-end text-xs text-gray-300 dark:text-gray-600`}>—</span>;
  const faixa = faixaDoAtingimento(p);
  const Icone = p >= 1 ? TrendingUp : TrendingDown;
  const dica = detalhe && detalhe.ritmoKwhDia != null
    ? `Ritmo dos últimos 7 dias: ${kwhCurto(detalhe.ritmoKwhDia)}/dia.${detalhe.precisaKwhDia ? ` Para bater a meta precisaria de ${kwhCurto(detalhe.precisaKwhDia)}/dia.` : ''}`
    : undefined;
  return (
    <span className={`${className} items-center justify-end gap-1 text-sm tabular-nums ${COR_FAIXA[faixa].texto} ${forte ? 'font-bold' : 'font-semibold'}`} title={dica}>
      <span className="text-[11px] font-normal text-gray-400 md:hidden">fecha em</span>
      <Icone size={13} aria-hidden /> {pct(p)}
    </span>
  );
};

/**
 * A resposta para "isso é bom ou ruim?": se o ritmo dos últimos 7 dias continuar,
 * onde o mês fecha — e, se não bate, quanto precisaria gerar por dia.
 */
const Veredito: React.FC<{ p: ReturnType<typeof totalProjecao>; nomeMes: string }> = ({ p, nomeMes }) => {
  if (p.projecao == null) return null;
  const bate = p.projecao >= 1;
  const perto = !bate && p.projecao >= 0.95;
  const esforco = p.precisaKwhDia && p.ritmoKwhDia ? p.precisaKwhDia / p.ritmoKwhDia - 1 : null;
  const tom = bate
    ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-900/25 dark:text-emerald-200'
    : perto
      ? 'bg-amber-50 text-amber-900 dark:bg-amber-900/25 dark:text-amber-200'
      : 'bg-rose-50 text-rose-900 dark:bg-rose-900/25 dark:text-rose-200';
  const Icone = bate ? TrendingUp : TrendingDown;
  return (
    <div className={`mx-5 mb-4 flex items-start gap-3 rounded-xl px-4 py-3 ${tom}`}>
      <Icone size={18} className="mt-0.5 shrink-0" aria-hidden />
      <div className="text-sm leading-relaxed">
        <p>
          <strong className="font-semibold">
            {bate ? 'Vai bater a meta.' : perto ? 'Fica perto, mas não bate.' : 'Não vai bater a meta.'}
          </strong>{' '}
          No ritmo dos últimos 7 dias, {nomeMes} fecha em <strong className="font-semibold tabular-nums">{pct(p.projecao)}</strong> da meta
          ({kwhCurto(p.projetadoKwh)} de {kwhCurto(p.metaMesKwh)}).
        </p>
        {!bate && p.precisaKwhDia != null && p.diasRestantes > 0.5 && (
          <p className="mt-0.5 text-xs opacity-80">
            Para chegar a 100%, as usinas precisariam gerar {kwhCurto(p.precisaKwhDia)} por dia até o fim do mês — hoje fazem {kwhCurto(p.ritmoKwhDia)} por dia
            {esforco != null && esforco > 0 ? ` (${pct(esforco)} a mais)` : ''}.
          </p>
        )}
      </div>
    </div>
  );
};

// ─────────────── mês a mês ───────────────

const Celula: React.FC<{ c: CelulaGeracao | undefined; rotulo: string; mes: string; forte?: boolean }> = ({ c, rotulo, mes, forte }) => {
  if (!c || !c.comparavel || c.atingimento == null) {
    return (
      <span className={`block rounded-md px-1 py-1.5 text-center text-xs ${FUNDO_FAIXA.sem_meta}`} title={`${rotulo} · ${rotuloMes(mes)}: sem dado completo no mês`}>
        —
      </span>
    );
  }
  return (
    <span
      className={`block rounded-md px-1 py-1.5 text-center text-xs tabular-nums ${forte ? 'font-bold' : 'font-semibold'} ${FUNDO_FAIXA[faixaDoAtingimento(c.atingimento)]}`}
      title={`${rotulo} · ${rotuloMes(mes)}: ${kwhCurto(c.realKwh)} de ${kwhCurto(c.metaKwh)}`}
    >
      {pct(c.atingimento)}
    </span>
  );
};

const MesAMes: React.FC<{
  usinas: UsinaCeo[];
  conjuntos: string[];
  matriz: ReturnType<typeof matrizGeracao>;
  mesAtualIso: string;
}> = ({ usinas, conjuntos, matriz, mesAtualIso }) => (
  <div className="px-5 pb-4">
    {/* No celular a tabela rola de lado e o nome da usina fica preso à esquerda. */}
    <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[720px] border-separate border-spacing-0 text-sm">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="sticky left-0 z-10 bg-white py-2 pr-3 text-left dark:bg-gray-800">Usina</th>
            {matriz.meses.map((m) => (
              <th key={m} className="px-1 py-2 text-center font-semibold normal-case tracking-normal">
                {rotuloMes(m)}
                {m === mesAtualIso && <span className="block text-[10px] font-normal text-gray-400">até hoje</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {conjuntos.map((nome) => (
            <React.Fragment key={nome}>
              <tr>
                <th className="sticky left-0 z-10 bg-white pb-0.5 pr-3 pt-3 text-left text-xs font-bold uppercase tracking-wide text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  {nome}
                </th>
                {matriz.meses.map((m) => (
                  <td key={m} className="px-1 pb-0.5 pt-3"><Celula c={matriz.porConjunto[nome]?.[m]} rotulo={nome} mes={m} forte /></td>
                ))}
              </tr>
              {usinas.filter((u) => u.conjunto === nome).map((u) => (
                <tr key={u.id}>
                  <th className="sticky left-0 z-10 whitespace-nowrap bg-white py-0.5 pl-3 pr-3 text-left text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {u.nome}
                  </th>
                  {matriz.meses.map((m) => (
                    <td key={m} className="px-1 py-0.5"><Celula c={matriz.porUsina[u.id]?.[m]} rotulo={u.nome} mes={m} /></td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr>
            <th className="sticky left-0 z-10 border-t border-gray-200 bg-white pr-3 pt-3 text-left text-xs font-bold uppercase tracking-wide text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              Todas
            </th>
            {matriz.meses.map((m) => (
              <td key={m} className="border-t border-gray-200 px-1 pt-3 dark:border-gray-700"><Celula c={matriz.total[m]} rotulo="Todas as usinas" mes={m} forte /></td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-gray-500 dark:text-gray-400">
      {([
        ['acima', '100% ou mais'],
        ['no_alvo', '95–99%'],
        ['abaixo', '85–94%'],
        ['critico', 'abaixo de 85%'],
        ['sem_meta', 'sem dado completo'],
      ] as const).map(([f, r]) => (
        <span key={f} className="inline-flex items-center gap-1.5">
          <span className={`h-3 w-5 rounded ${FUNDO_FAIXA[f]}`} aria-hidden /> {r}
        </span>
      ))}
    </p>
  </div>
);

export default GeracaoDoMes;
